import DefaultLayout from "@/layouts/DefaultLayout";
import KDriverForm from "kinnect-front/KYard/Drivers/KDriverForm";
import { Crudable, FetchesData, Pageable } from "kinnect-mixins";
import { KActionDialog, KDateTimeRange, KRoutableTreeview } from "kinnect-general";
import { YardDriver, YardTrip } from "kinnect-models";
import { getDriverTimesOff } from "@/utils/http-calls";
import { sortByKey } from "kinnect-helpers";

export default {
	name: "YardDriverPage",

	metaInfo() {
		const title = this.editing && this.fetched
			? `Yard - Drivers - ${this.driver.user.name}`
			: "Yard - Drivers - Add a Driver";

		return { title: this.fetched ? title : null };
	},

	props: { idDriver: Number },

	mixins: [
		Crudable,
		FetchesData,
		Pageable
	],

	data: () => ( {
		crud: "yard_drivers",
		deleteDialogOpen: false,
		driver: null
	} ),

	computed: {
		editing() {
			return Boolean( this.idDriver );
		},

		canBrowseYardTrips() {
			return this.$can( "browse_yard_trips" );
		},

		genDeleteDialog() {
			if ( this.editing && this.driver && this.canDelete ) {
				return <KActionDialog
					confirmButtonColor="error"
					confirmButtonText="Delete"
					loading={this.$wait.is( "delete driver" )}
					onConfirm={() => this.driver.delete()}
					redirect={[ "back", { name: "yardDrivers" } ]}
					title="Delete Driver"
					vModel={this.deleteDialogOpen}
				>
					Are you sure you want to delete the driver <strong>{this.driver.user.name}</strong>?
				</KActionDialog>;
			}
		},

		genTimeOffTree() {
			return <KRoutableTreeview
				itemIcon="event"
				loadChildren={this.fetchDriverTimesOff}
				on={{
					"click:item": timeOff => this.$router.push( {
						name: "yardDriverTimeOff",
						params: {
							idDriver: timeOff.idDriver,
							idTimeOff: timeOff.id
						}
					} )
				}}
				scopedSlots={{
					name: timeOff => <KDateTimeRange
						allDay={Boolean( timeOff.allDay )}
						endDate={timeOff.endDate}
						endTime={timeOff.endTime}
						startDate={timeOff.startDate}
						startTime={timeOff.startTime}
					></KDateTimeRange>
				}}
				title="Time Off"
			></KRoutableTreeview>;
		},

		genActiveTripsTree() {
			return this.canBrowseYardTrips && <KRoutableTreeview
				itemIcon="directions"
				loadChildren={this.fetchDriverActiveTrips}
				on={{
					"click:item": trip => this.$router.push( {
						name: "yardTrip",
						params: { idTrip: trip.id }
					} )
				}}
				scopedSlots={{
					name: trip =>
						<KDateTimeRange
							endDate={trip.deliveryDate}
							endTime={trip.estReturnTime}
							startDate={trip.deliveryDate}
							startTime={trip.estDepartureTime}
						></KDateTimeRange>
				}}
				title="Active Trips"
			></KRoutableTreeview>;
		},

		genCompletedTripsTree() {
			return this.canBrowseYardTrips && <KRoutableTreeview
				itemIcon="directions"
				loadChildren={this.fetchDriverCompletedTrips}
				on={{
					"click:item": trip => this.$router.push( {
						name: "yardTrip",
						params: { idTrip: trip.id }
					} )
				}}
				scopedSlots={{
					name: trip =>
						<KDateTimeRange
							endDate={trip.deliveryDate}
							endTime={trip.completeTime}
							startDate={trip.deliveryDate}
							startTime={trip.loadingTime}
						></KDateTimeRange>
				}}
				title="Completed Trips"
			></KRoutableTreeview>;
		},

		genSidebar() {
			if ( !this.editing ) {
				return;
			}

			return <template slot="leftSidebar">
				{this.genTimeOffTree}
				{this.genActiveTripsTree}
				{this.genCompletedTripsTree}
			</template>;
		},

		toolbarActions() {
			const actions = [];

			if ( this.editing && this.canDelete ) {
				actions.push( {
					icon: "delete",
					onClick: () => this.deleteDialogOpen = true,
					text: "Delete Driver",
					tooltip: "Delete this driver"
				} );
			}

			if ( this.editing && this.canEdit ) {
				actions.push( {
					icon: "add_circle",
					text: "Add Time off",
					to: {
						name: "yardDriverTimeOffAdd",
						params: { idDriver: this.idDriver }
					},
					tooltip: "Add time off for this driver"
				} );
			}

			return actions;
		}
	},

	methods: {
		async fetchDriverTimesOff() {
			if ( this.editing ) {
				let timeOff = await getDriverTimesOff( this.driver.id );

				timeOff = sortByKey( timeOff, "startDate" ).reverse();

				return timeOff;
			}
		},

		fetchDriverActiveTrips() {
			if ( this.editing ) {
				return YardTrip
				.where( "complete", false )
				.where( "drivers.id", this.driver.id )
				.get();
			}
		},

		fetchDriverCompletedTrips() {
			if ( this.editing ) {
				return YardTrip
				.where( "complete", true )
				.where( "drivers.id", this.driver.id )
				.get();
			}
		},

		async fetchDriver() {
			if ( this.editing ) {
				this.driver = await YardDriver.findPageModel( this.idDriver );
			}
		},

		async fetch() {
			await this.fetchDriver();
		}
	},

	render() {
		return <DefaultLayout maxWidth={this.editing ? "900px" : "600px"}>
			<KDriverForm
				driver={this.driver}
				loading={this.$wait.is( "find driver" )}
				readonly={this.editing ? !this.canEdit : !this.canAdd}
			></KDriverForm>

			{this.genDeleteDialog}

			{this.genSidebar}
		</DefaultLayout>;
	}
};
